import React, { useState } from "react";
import {
    Button,
    ButtonGroup,
    Table,
    ControlLabel
} from "react-bootstrap";
import moment from "moment/moment";
import toast, { Toaster } from "react-hot-toast";
import locale from "../../../locale";
import { createEpisode, deleteEpisode, getEpisodes, updateEpisode, clearEpisodesList } from "../../../../api/cp";
import Request from "request";
import Loading from "../../Loading";
import { Event } from "../../Comet";
import Select from "react-select";
import { checkIntersection, getCurrentTZdayEnd } from "../../../helpers/timeHelper";
import EpisodeRow from "./components/EpisodeRow";

const getEpisodesByDay = (episodes, streamDay) => {
    return episodes?.filter(({ timeStart }) => moment(streamDay).isSame(moment.unix(timeStart), 'day')) || [];
}

export default ({ episodes, setEpisodes, streamDaysOptions, event, stream, onEdit }) => {

    const [dayFilter, setDayFilter] = useState(null);
    const [loading, setLoading] = useState(false);

    const updateEpisodeProperty = (episodeID, newData) => {
        setEpisodes(episodes => episodes.map(episode => episode.id === episodeID ? { ...episode, ...newData } : episode))
    }

    const handleGetEpisodes = () => {
        getEpisodes(stream.id)
            .then(episodes => setEpisodes(episodes?.sort((a, b) => a.timeStart === b.timeStart ? a.id - b.id : a.timeStart - b.timeStart) || []))
            ;
    }

    const handleSave = (episode, data) => {
        updateEpisode(episode.id, data)
            .then((episodeData) => {
                setEpisodes((prevEpisodes) => {
                    let eps = prevEpisodes.slice();
                    let index = eps.findIndex(({ id }) => id === episodeData.id);
                    if (-1 === index) {
                        eps.push(episodeData);
                    } else {
                        eps[index] = Object.assign({}, eps[index], episodeData);
                    }

                    return eps;
                });
            })
            .catch((error) => {
                if (error.message) {
                    alert(error.message);
                    handleGetEpisodes();
                }
            })
            ;
    }
    const handleCreate = (data, streamDay) => {
        const streamDayEpisodes = getEpisodesByDay(episodes, streamDay);
        const streamDayEnd = getCurrentTZdayEnd(streamDay, 'unix');

        let defaultStartTime = streamDayEnd - 3600 * 15;
        if (defaultStartTime < stream.timeStart) {
            defaultStartTime = stream.timeStart;
        } else if (defaultStartTime > stream.timeStop) {
            defaultStartTime = stream.timeStop - 3600;
        }

        const timeStart = streamDayEpisodes?.length ? streamDayEpisodes.reduce((time, { timeStop }) => Math.max(time, timeStop), episodes[0].timeStop) : defaultStartTime;
        let timeStop = timeStart + 3600;
        if (timeStop > stream.timeStop) {
            timeStop = stream.timeStop;
        } else if (timeStop > streamDayEnd) {
            timeStop = streamDayEnd
        }

        const order = stream.episode.reduce((last, { order }) => Math.max(last, order), 0) + 1;

        createEpisode(stream.id, {
            timeStart,
            timeStop,
            order,
            ...data
        })
            .then((episode) => {
                handleCometEpisodeUpdated(episode);
            });
    }
    const handleDelete = (episode) => {
        return deleteEpisode(episode.id)
            .then(() => {
                setEpisodes((prevEpisodes) => prevEpisodes.slice().filter(({ id }) => id !== episode.id));
            })
            ;
    }

    const handleCometEpisodeCreated = (episode) => {
        handleCometEpisodeUpdated(episode);
    }

    const handleCometEpisodeUpdated = (episode) => {
        setEpisodes((prevEpisodes) => {
            const newEpisodes = prevEpisodes?.slice() ?? [];
            const index = newEpisodes.findIndex(({ id }) => id === episode.id);
            if (-1 === index) {
                newEpisodes.push(episode);
            } else {
                newEpisodes[index] = { ...newEpisodes[index], ...episode };
            }

            return newEpisodes.sort((a, b) => a.timeStart === b.timeStart ? a.id - b.id : a.timeStart - b.timeStart);
        });
    }

    const handleCometEpisodeDeleted = (episode) => {
        setEpisodes((prevEpisodes) => prevEpisodes.filter(({ id }) => id !== episode.id));
    }

    const handleImportExcel = (e) => {
        const fileInput = document.createElement('input');
        fileInput.setAttribute('type', 'file');
        fileInput.setAttribute('accept', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        fileInput.addEventListener('change', (e) => {
            const file = e.target.files[0];
            if (!file) {
                return;
            }
            setLoading(true);

            Request.post(`/api/cp/event/stream/${stream.streamKey}/importEpisodes`, {
                file,
                timezone: moment().format('Z').replace(':', ''),
            })
                .then((data) => {
                    setEpisodes((prevEpisodes) => [...prevEpisodes, ...data]);
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setTimeout(() => {
                        setLoading(false);
                    }, 100);
                });

        }, false);
        fileInput.click();
    }

    const handleMove = (episode) => {
        setEpisodes(null);
        getEpisodes(stream.id)
            .then(setEpisodes);
    }

    const clearEpisodes = () => {
        if (confirm(locale('episodes.clear_question'))) {
            clearEpisodesList(stream.id).then(() => {
                setEpisodes([]);
            });
        }
    }

    const renderOutOfRangeEpisodes = (limit) => {
        const firstDay = streamDaysOptions[0].value;
        const lastDay = streamDaysOptions[streamDaysOptions.length - 1].value;
        const outOfRangeEpisodes = {
            before: episodes.filter(({ timeStart }) => timeStart < moment(firstDay).unix()),
            after: episodes.filter(({ timeStart }) => timeStart > moment(lastDay).unix() + 3600 * 24),
        }

        const resultEpisodes = outOfRangeEpisodes[limit] || [];

        if (dayFilter || !resultEpisodes.length) return null;

        return <EpisodesTable
            stream={stream}
            tableEpisodes={resultEpisodes}
            streamDaysOptions={streamDaysOptions}
            episodes={episodes}
            dayFilter={dayFilter}
            streams={event.stream.filter((s) => s.id !== stream.id)}
            updateEpisodeProperty={updateEpisodeProperty}
            handleSave={handleSave}
            handleDelete={handleDelete}
            onEdit={onEdit}
            handleMove={handleMove}
            handleCreate={handleCreate}
        />
    }

    if (null === episodes) {
        return <Loading />
    }

    return (
        <div style={{ marginTop: 10 }}>
            <Event event="create:episode" handler={handleCometEpisodeCreated} />
            <Event event="update:episode" handler={handleCometEpisodeUpdated} />
            <Event event="delete:episode" handler={handleCometEpisodeDeleted} />
            <div style={{ float: 'right', textAlign: 'right' }}>
                <Button onClick={handleImportExcel.bind(this)}>Импорт из Excel</Button>
                {' '}
                <Button>
                    <a href={`/api/cp/excel/episodes/sample.xlsx`} style={{ textDecoration: 'none', color: 'inherit' }}>
                        Скачать шаблон Еxcel
                    </a>
                </Button>
            </div>

            {streamDaysOptions.length > 1 &&
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', margin: '0 1rem' }}>
                    <ControlLabel>
                        Дата
                    </ControlLabel>
                    <Select
                        isClearable
                        value={streamDaysOptions.find(({ value }) => moment(value).isSame(dayFilter))}
                        onChange={(value) => setDayFilter(value?.value || null)}
                        options={streamDaysOptions}
                        placeholder="Выберите дату"
                    />
                </div>
            }

            <div style={{ margin: '1rem 0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                    Время работы зала: <b>{streamDaysOptions.length > 1 && moment.unix(stream.timeStart).format('DD.MM.YYYY') + " "}{moment.unix(stream.timeStart).format('HH:mm:ss')} - {streamDaysOptions.length > 1 && moment.unix(stream.timeStop).format('DD.MM.YYYY') + " "}{moment.unix(stream.timeStop).format('HH:mm:ss')}</b>
                </div>
                {!!episodes.length &&
                    <Button
                        bsStyle="danger"
                        onClick={clearEpisodes}
                    >
                        Удалить все эпизоды
                    </Button>
                }
            </div>
            {renderOutOfRangeEpisodes('before')}
            {streamDaysOptions.map(streamDay => {
                if (dayFilter && !moment(dayFilter).isSame(moment(streamDay.value), 'day')) return null;
                const filteredEpisodes = episodes.filter(({ timeStart }) => moment(streamDay.value).isSame(moment.unix(timeStart), 'day'));

                return <div key={streamDay.label}>
                    <div style={{ fontSize: '2rem', padding: '1rem 0.5rem 0' }}>{streamDay.label}</div>
                    <EpisodesTable
                        isInRange
                        renderDate={streamDaysOptions.length > 1}
                        stream={stream}
                        streamDay={streamDay}
                        tableEpisodes={filteredEpisodes}
                        streamDaysOptions={streamDaysOptions}
                        episodes={episodes}
                        dayFilter={dayFilter}
                        streams={event.stream.filter((s) => s.id !== stream.id)}
                        updateEpisodeProperty={updateEpisodeProperty}
                        handleSave={handleSave}
                        handleDelete={handleDelete}
                        onEdit={onEdit}
                        handleMove={handleMove}
                        handleCreate={handleCreate}
                    />
                </div>
            })}
            {renderOutOfRangeEpisodes('after')}
            <Toaster position="top-left" />
        </div>
    );
}

const EpisodesTable = ({
    isInRange,
    renderDate,
    streamDay = {},
    streamDaysOptions,
    stream,
    tableEpisodes,
    dayFilter,
    episodes,
    streams,
    updateEpisodeProperty,
    handleSave,
    handleDelete,
    onEdit,
    handleMove,
    handleCreate,
}) => {

    const isIntersected = (episode) => {
        const episodeStartEnd = { start: episode.timeStart, end: episode.timeStop };
        return episodes.filter(({ id }) => id !== episode.id).some(({ timeStart, timeStop }) => checkIntersection(episodeStartEnd, { start: timeStart, end: timeStop }));
    }

    const lastSlotTaken = (day) => {
        const streamDayEpisodes = getEpisodesByDay(episodes, day);
        const streamDayEnd = getCurrentTZdayEnd(day, 'unix');
        const lastEpisode = streamDayEpisodes[streamDayEpisodes.length - 1];
        return lastEpisode && (stream.timeStop - lastEpisode.timeStop === 0 || streamDayEnd - lastEpisode.timeStop === 0);
    }

    return <Table>
        <thead>
            <tr>
                {(!isInRange || renderDate) &&
                    <th style={{ width: 150 }}>Дата</th>
                }
                <th style={{ width: 200 }}>Промежуток</th>
                <th style={{ width: 100 }}>Длина</th>
                <th>Название</th>
                <th style={{ width: 100 }}>Ключ</th>
                <th style={{ width: 172 }} />
                <th style={{ width: 80 }}></th>
            </tr>
        </thead>
        <tbody>
            {tableEpisodes
                .map((episode, index) => (
                    <EpisodeRow
                        key={index}
                        episode={episode}
                        episodes={episodes}
                        isIntersected={isIntersected(episode)}
                        updateEpisodeProperty={updateEpisodeProperty}
                        isFiltered={!!dayFilter}
                        streamDaysOptions={streamDaysOptions}
                        streamTimeStart={stream.timeStart}
                        streamTimeStop={stream.timeStop}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        onEdit={onEdit}
                        onMove={handleMove}
                        toast={toast}
                        streams={streams}
                    />
                )
                )}
        </tbody>
        {isInRange &&
            <tfoot>
                <tr>
                    <td colSpan={20} style={{ textAlign: 'right' }}>
                        <ButtonGroup>
                            <Button disabled={lastSlotTaken(streamDay.value)} onClick={() => handleCreate({ nameRu: 'Эпизод' }, streamDay.value)}>Добавить эпизод</Button>
                            <Button disabled={lastSlotTaken(streamDay.value)} onClick={() => handleCreate({ nameRu: 'Перерыв', isDelay: true }, streamDay.value)}>Добавить перерыв</Button>
                        </ButtonGroup>
                    </td>
                </tr>
            </tfoot>
        }
    </Table>
}